const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const API_KEY = import.meta.env.VITE_API_KEY

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL
    this.apiKey = API_KEY
  }

  async makeRequest(action, data = {}) {
    try {
      const requestBody = {
        action,
        ...data
      }

      // Debug logging only in development
      if (import.meta.env.DEV) {
        console.log('API Request:', action)
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey
        },
        body: JSON.stringify(requestBody)
      })

      if (import.meta.env.DEV) {
        console.log('Response status:', response.status)
      }

      if (!response.ok) {
        // Read the response body only once
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const responseText = await response.text()
          // Only log errors in development
          if (import.meta.env.DEV) {
            console.log('Error response:', responseText.substring(0, 200))
          }
          
          // Try to parse as JSON
          const errorData = JSON.parse(responseText)
          errorMessage = errorData.message || errorMessage
        } catch (parseError) {
          // Use default error message if parsing fails
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()
      
      // Check if the API returned an error
      if (result.status === 'error' || result.success === false) {
        throw new Error(result.message || 'API request failed')
      }
      
      return result
    } catch (error) {
      throw error
    }
  }

  async makeAuthenticatedRequest(action, data = {}, isAdmin = false, isSuperAdmin = false) {
    try {
      let token
      
      if (isSuperAdmin) {
        token = sessionStorage.getItem('superAdminToken')
      } else if (isAdmin) {
        token = sessionStorage.getItem('adminToken')
      } else {
        token = sessionStorage.getItem('customerToken')
      }
      
      if (!token) {
        throw new Error('No authentication token found')
      }

      const requestBody = {
        action,
        ...data
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
          'Session-Auth-Token': token
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const responseText = await response.text()
          const errorData = JSON.parse(responseText)
          errorMessage = errorData.message || errorMessage
        } catch (parseError) {
          // Use default error message if parsing fails
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()
      
      // Check if the API returned an error
      if (result.status === 'error' || result.success === false) {
        throw new Error(result.message || 'API request failed')
      }
      
      return result
    } catch (error) {
      throw error
    }
  }

  // MG Management
  async getMGList() {
    return this.makeRequest('GET-MG-LIST')
  }

  // Member Authentication (using member number and PIN)
  async memberLogin(memberNo, pin) {
    try {
      return this.makeRequest('CUSTOMER-LOGIN', {
        memberNo,
        pin
      })
    } catch (error) {
      console.error('Member login failed:', error)
      throw error
    }
  }

  // Legacy Customer Authentication (DEPRECATED)
  async customerRegister(data) {
    return this.makeRequest('CUSTOMER-REGISTER', data)
  }

  async customerLogin(data) {
    return this.makeRequest('CUSTOMER-LOGIN', data)
  }

  // Admin Authentication
  async adminLogin(data) {
    return this.makeRequest('ADMIN-LOGIN', data)
  }

  // Super Admin Authentication  
  async superAdminLogin(data) {
    return this.makeRequest('SUPER-ADMIN-LOGIN', data)
  }

  // Server-side session logout
  async serverLogout(isAdmin = false, isSuperAdmin = false) {
    try {
      return await this.makeAuthenticatedRequest('LOGOUT', {}, isAdmin, isSuperAdmin)
    } catch (error) {
      // Swallow errors on logout — we're clearing local state regardless
      console.warn('Server logout failed (non-critical):', error.message)
      return { status: 'SUCCESS' }
    }
  }

  // Beacon-based logout (for beforeunload — fire-and-forget)
  beaconLogout(isAdmin = false, isSuperAdmin = false) {
    let token
    if (isSuperAdmin) {
      token = sessionStorage.getItem('superAdminToken')
    } else if (isAdmin) {
      token = sessionStorage.getItem('adminToken')
    } else {
      token = sessionStorage.getItem('customerToken')
    }
    if (!token) return

    const payload = JSON.stringify({ action: 'LOGOUT' })
    const blob = new Blob([payload], { type: 'application/json' })
    
    // sendBeacon doesn't support custom headers, so we append token to URL
    // The backend can also check the body, but we'll use a query param fallback
    try {
      navigator.sendBeacon(
        `${this.baseUrl}?session_token=${encodeURIComponent(token)}&api_key=${encodeURIComponent(this.apiKey)}`,
        blob
      )
    } catch (e) {
      // Last-resort: fire-and-forget fetch
      fetch(this.baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey, 'Session-Auth-Token': token },
        body: payload,
        keepalive: true
      }).catch(() => {})
    }
  }

  // Get marketplace stats (SuperAdmin only)
  async getKataleMarketplaceStats() {
    return this.makeAuthenticatedRequest('KATALE-GET-MARKETPLACE-STATS', {}, false, true)
  }

  // Sync API users (SuperAdmin only)
  async syncApiUsers() {
    return this.makeAuthenticatedRequest('SYNC-API-USERS', {}, false, true)
  }

  // Financial Data
  async submitFinancialData(data) {
    return this.makeAuthenticatedRequest('SUBMIT-FINANCIAL-DATA', data)
  }

  async getCustomerFinancialStatements(customerId) {
    return this.makeAuthenticatedRequest('GET-CUSTOMER-FINANCIAL-STATEMENTS', 
      { customerId: customerId })
  }

  async getCustomerDashboardData() {
    return this.makeAuthenticatedRequest('GET-CUSTOMER-DASHBOARD', {})
  }

  // Customer Loans
  async getCustomerLoans() {
    return this.makeAuthenticatedRequest('GET-CUSTOMER-LOANS', {})
  }

  async getCompanyCustomerLoans(customerId, companyId) {
    return this.makeAuthenticatedRequest('GET-COMPANY-CUSTOMER-LOANS', 
      { customer_id: customerId, company_id: companyId })
  }

  // Admin Dashboard
  async getActiveLoans() {
    return this.makeAuthenticatedRequest('GET-ACTIVE-LOANS', {}, true)
  }

  async searchCustomers(searchTerm) {
    return this.makeAuthenticatedRequest('SEARCH-CUSTOMERS', 
      { search_term: searchTerm }, true)
  }

  async getCustomerDetails(customerId) {
    return this.makeAuthenticatedRequest('GET-CUSTOMER-DETAILS', 
      { customer_id: customerId }, true)
  }

  // Dashboard Stats
  async getDashboardStats() {
    return this.makeAuthenticatedRequest('GET-DASHBOARD-STATS', {}, true)
  }

  // Get all registered active tenants with customer counts
  async getRegisteredActiveTenants() {
    return this.makeAuthenticatedRequest('GET-TENANT-ACTIVE-CUSTOMERS', {}, false, true)
  }

  // Financial Statements
  async getFinancialStatements() {
    return this.makeAuthenticatedRequest('GET-FINANCIAL-STATEMENTS', {}, true)
  }

  // Member Product Profiling (Admin + SuperAdmin)
  async getMemberProductProfiles(filters = {}) {
    const isAdmin = !filters.isSuperAdmin
    return this.makeAuthenticatedRequest('GET-MEMBER-PRODUCT-PROFILES', {
      product_name: filters.productName || '',
      reporting_period: filters.reportingPeriod || '',
      mg_code: filters.mgCode || ''
    }, isAdmin, !!filters.isSuperAdmin)
  }

  async getProductReport(productName, reportingPeriod = '', isSuperAdmin = false) {
    return this.makeAuthenticatedRequest('GET-PRODUCT-REPORT', {
      product_name: productName,
      reporting_period: reportingPeriod
    }, !isSuperAdmin, isSuperAdmin)
  }

  // Member Self-Service Financial Statements
  async getMyFinancialStatements() {
    return this.makeAuthenticatedRequest('GET-MY-FINANCIAL-STATEMENTS', {})
  }

  async updateMyFinancialStatement(statementId, financialData, reportingPeriod = null) {
    const payload = { statement_id: statementId, financial_data: financialData }
    if (reportingPeriod) payload.reporting_period = reportingPeriod
    return this.makeAuthenticatedRequest('UPDATE-MY-FINANCIAL-STATEMENT', payload)
  }

  // Super Admin methods - pass tenantDb to ensure correct MG database is queried
  async searchCustomersGlobal(searchTerm) {
    return this.makeAuthenticatedRequest('SUPER-ADMIN-SEARCH', 
      { searchTerm: searchTerm }, false, true)
  }

  async getCustomerShares(customerId, tenantDb = null) {
    const params = { customerId: customerId }
    if (tenantDb) params.tenantDb = tenantDb
    return this.makeAuthenticatedRequest('GET-CUSTOMER-SHARES', params, false, true)
  }

  async getCustomerLoansByCustomerId(customerId, tenantDb = null) {
    const params = { customerId: customerId }
    if (tenantDb) params.tenantDb = tenantDb
    return this.makeAuthenticatedRequest('GET-CUSTOMER-LOANS', params, false, true)
  }

  async getCustomerFinancialStatementsByMemberNo(memberNo, tenantDb = null) {
    const params = { memberNo: memberNo }
    if (tenantDb) params.tenantDb = tenantDb
    return this.makeAuthenticatedRequest('GET-CUSTOMER-FINANCIAL-STATEMENTS', params, false, true)
  }

  // ==========================================
  // MG KATALE MARKETPLACE METHODS
  // ==========================================

  // Get product categories
  async getKataleCategories() {
    return this.makeAuthenticatedRequest('KATALE-GET-CATEGORIES', {})
  }

  // Get products with optional filters
  async getKataleProducts(filters = {}) {
    return this.makeAuthenticatedRequest('KATALE-GET-PRODUCTS', filters)
  }

  // Get single product
  async getKataleProduct(productId) {
    return this.makeAuthenticatedRequest('KATALE-GET-PRODUCT', { product_id: productId })
  }

  // Add new product (seller)
  async addKataleProduct(productData) {
    return this.makeAuthenticatedRequest('KATALE-ADD-PRODUCT', productData)
  }

  // Update product
  async updateKataleProduct(productId, productData) {
    return this.makeAuthenticatedRequest('KATALE-UPDATE-PRODUCT', { 
      product_id: productId, 
      ...productData 
    })
  }

  // Delete product
  async deleteKataleProduct(productId) {
    return this.makeAuthenticatedRequest('KATALE-DELETE-PRODUCT', { product_id: productId })
  }

  // Get seller's own products
  async getKataleMyProducts(sellerMemNo, sellerMgCode) {
    return this.makeAuthenticatedRequest('KATALE-GET-MY-PRODUCTS', {
      seller_mem_no: sellerMemNo,
      seller_mg_code: sellerMgCode
    })
  }

  // Shopping cart methods
  async getKataleCart(buyerMemNo, buyerMgCode) {
    return this.makeAuthenticatedRequest('KATALE-GET-CART', {
      buyer_mem_no: buyerMemNo,
      buyer_mg_code: buyerMgCode
    })
  }

  async addToKataleCart(productId, buyerMemNo, buyerMgCode, quantity = 1) {
    return this.makeAuthenticatedRequest('KATALE-ADD-TO-CART', {
      product_id: productId,
      buyer_mem_no: buyerMemNo,
      buyer_mg_code: buyerMgCode,
      quantity: quantity
    })
  }

  async updateKataleCartItem(cartItemId, quantity) {
    return this.makeAuthenticatedRequest('KATALE-UPDATE-CART-ITEM', {
      cart_item_id: cartItemId,
      quantity: quantity
    })
  }

  async removeFromKataleCart(cartItemId) {
    return this.makeAuthenticatedRequest('KATALE-REMOVE-FROM-CART', {
      cart_item_id: cartItemId
    })
  }

  // Checkout
  async kataleCheckout(buyerMemNo, buyerMgCode, deliveryAddress, deliveryFee = 0) {
    return this.makeAuthenticatedRequest('KATALE-CHECKOUT', {
      buyer_mem_no: buyerMemNo,
      buyer_mg_code: buyerMgCode,
      delivery_address: deliveryAddress,
      delivery_fee: deliveryFee
    })
  }

  // Orders
  async getKataleOrders(memNo, mgCode, type = 'purchases') {
    return this.makeAuthenticatedRequest('KATALE-GET-ORDERS', {
      mem_no: memNo,
      mg_code: mgCode,
      type: type
    })
  }

  async getKataleOrder(orderId) {
    return this.makeAuthenticatedRequest('KATALE-GET-ORDER', { order_id: orderId })
  }

  async updateKataleOrderStatus(orderId, status) {
    return this.makeAuthenticatedRequest('KATALE-UPDATE-ORDER-STATUS', {
      order_id: orderId,
      status: status
    })
  }

  async updateKataleDeliveryStatus(orderId, deliveryStatus, deliveryNotes, sellerMemNo, sellerMgCode) {
    return this.makeAuthenticatedRequest('KATALE-UPDATE-DELIVERY-STATUS', {
      order_id: orderId,
      delivery_status: deliveryStatus,
      delivery_notes: deliveryNotes || null,
      seller_mem_no: sellerMemNo,
      seller_mg_code: sellerMgCode
    })
  }

  // Savings balance for Katale
  async getKataleSavingsBalance(memNo, mgCode) {
    return this.makeAuthenticatedRequest('KATALE-GET-SAVINGS-BALANCE', {
      mem_no: memNo,
      mg_code: mgCode
    })
  }

  // Float limit check (for MG admins)
  async checkKataleFloatLimit(mgKey) {
    return this.makeAuthenticatedRequest('KATALE-CHECK-FLOAT-LIMIT', { mg_key: mgKey }, true)
  }

  // MG position (for MG admins)
  async getKataleMgPosition(mgKey) {
    return this.makeAuthenticatedRequest('KATALE-GET-MG-POSITION', { mg_key: mgKey }, true)
  }

  // Settlement batches (for MG admins)
  async getKataleSettlementBatches(mgKey = null, status = null) {
    return this.makeAuthenticatedRequest('KATALE-GET-SETTLEMENT-BATCHES', {
      mg_key: mgKey,
      status: status
    }, true)
  }

  // Create settlement batch (for MG admins)
  async createKataleSettlementBatch(fromMgCode, toMgCode) {
    return this.makeAuthenticatedRequest('KATALE-CREATE-SETTLEMENT-BATCH', {
      from_mg_code: fromMgCode,
      to_mg_code: toMgCode
    }, true)
  }

  // Confirm settlement (for MG admins)
  async confirmKataleSettlement(batchId, mgKey, bankReference = null, mobileMoneyReference = null) {
    return this.makeAuthenticatedRequest('KATALE-CONFIRM-SETTLEMENT', {
      batch_id: batchId,
      mg_key: mgKey,
      bank_reference: bankReference,
      mobile_money_reference: mobileMoneyReference
    }, true)
  }

  // Get UFCC commission for MG admin
  async getKataleUfccCommission(mgKey, startDate = null, endDate = null) {
    return this.makeAuthenticatedRequest('KATALE-GET-UFCC-COMMISSION', {
      mg_key: mgKey,
      start_date: startDate,
      end_date: endDate
    }, true)
  }

  // Settle UFCC commission (MG admin pays UFCC)
  async settleKataleUfccCommission(mgKey, amount, paymentReference, paymentMethod = 'bank_transfer') {
    return this.makeAuthenticatedRequest('KATALE-SETTLE-UFCC-COMMISSION', {
      mg_key: mgKey,
      amount: amount,
      payment_reference: paymentReference,
      payment_method: paymentMethod
    }, true)
  }

  // ==========================================
  // SUPERADMIN UFCC COMMISSION METHODS
  // ==========================================

  // Get all UFCC commissions across all MGs (SuperAdmin only)
  async getKataleAllUfccCommissions(startDate = null, endDate = null) {
    return this.makeAuthenticatedRequest('KATALE-GET-ALL-UFCC-COMMISSIONS', {
      start_date: startDate,
      end_date: endDate
    }, false, true)
  }

  // Confirm UFCC settlement receipt (SuperAdmin only)
  async confirmKataleUfccSettlementReceipt(settlementId, confirmationReference = '', notes = '') {
    return this.makeAuthenticatedRequest('KATALE-CONFIRM-UFCC-SETTLEMENT', {
      settlement_id: settlementId,
      confirmation_reference: confirmationReference,
      notes: notes
    }, false, true)
  }

  // ==========================================
  // ENHANCED SEARCH METHODS
  // ==========================================

  // Search products with advanced filters
  async searchKataleProducts(filters = {}) {
    return this.makeAuthenticatedRequest('KATALE-SEARCH-PRODUCTS', {
      search: filters.search || '',
      category_id: filters.categoryId || null,
      min_price: filters.minPrice || null,
      max_price: filters.maxPrice || null,
      condition: filters.condition || null,
      sort_by: filters.sortBy || 'relevance',
      page: filters.page || 1,
      limit: filters.limit || 20
    })
  }

  // ==========================================
  // ESCROW / BUYER PROTECTION METHODS
  // ==========================================

  // Confirm delivery as buyer (releases escrow funds)
  async confirmKataleDeliveryReceipt(orderId, buyerMemNo, buyerMgCode, confirmationType = 'accepted') {
    return this.makeAuthenticatedRequest('KATALE-CONFIRM-DELIVERY-RECEIPT', {
      order_id: orderId,
      buyer_mem_no: buyerMemNo,
      buyer_mg_code: buyerMgCode,
      confirmation_type: confirmationType // 'accepted' or 'rejected'
    })
  }

  // Raise a dispute on an order (buyer protection)
  async raiseKataleDispute(orderId, buyerMemNo, buyerMgCode, reason, description) {
    return this.makeAuthenticatedRequest('KATALE-RAISE-DISPUTE', {
      order_id: orderId,
      buyer_mem_no: buyerMemNo,
      buyer_mg_code: buyerMgCode,
      reason: reason,
      description: description
    })
  }

  // Get escrow status for an order
  async getKataleEscrowStatus(orderId) {
    return this.makeAuthenticatedRequest('KATALE-GET-ESCROW-STATUS', {
      order_id: orderId
    })
  }

  // ==========================================
  // SUPERADMIN CROSS-MG CLEARING HOUSE METHODS
  // ==========================================

  // Get all cross-MG settlement batches (SuperAdmin clearing house)
  async getKataleAllSettlementBatches(status = null) {
    return this.makeAuthenticatedRequest('KATALE-GET-ALL-SETTLEMENT-BATCHES', {
      status: status
    }, false, true)
  }

  // SuperAdmin approve/facilitate a cross-MG settlement
  async approveKataleCrossMgSettlement(batchId, notes = '', confirmationReference = '') {
    return this.makeAuthenticatedRequest('KATALE-APPROVE-CROSS-MG-SETTLEMENT', {
      batch_id: batchId,
      notes: notes,
      confirmation_reference: confirmationReference
    }, false, true)
  }

  // Get cross-MG transaction summary for clearing (SuperAdmin)
  async getKataleClearingSummary() {
    return this.makeAuthenticatedRequest('KATALE-GET-CLEARING-SUMMARY', {}, false, true)
  }

  // Lookup an order by reference number (SuperAdmin)
  async lookupKataleOrderByRef(reference) {
    return this.makeAuthenticatedRequest('KATALE-LOOKUP-ORDER-BY-REF', {
      reference: reference
    }, false, true)
  }

  // Process a refund for an order (SuperAdmin — only if escrow still held)
  async processKataleRefund(orderId, reason) {
    return this.makeAuthenticatedRequest('KATALE-PROCESS-REFUND', {
      order_id: orderId,
      reason: reason
    }, false, true)
  }

  // Resolve a dispute (SuperAdmin)
  async resolveKataleDispute(disputeId, resolution, notes) {
    return this.makeAuthenticatedRequest('KATALE-RESOLVE-DISPUTE', {
      dispute_id: disputeId,
      resolution: resolution,
      notes: notes
    }, false, true)
  }

  // Get all disputes (SuperAdmin)
  async getKataleAllDisputes(status = null) {
    return this.makeAuthenticatedRequest('KATALE-GET-ALL-DISPUTES', {
      status: status
    }, false, true)
  }

  // ==========================================
  // RATING & REVIEW METHODS
  // ==========================================

  // Get seller rating summary (public)
  async getKataleSellerPublicRating(sellerMemNo, sellerMgCode) {
    return this.makeAuthenticatedRequest('KATALE-GET-SELLER-PUBLIC-RATING', {
      seller_mem_no: sellerMemNo,
      seller_mg_code: sellerMgCode
    })
  }

  // Get product ratings
  async getKataleProductRatings(productId) {
    return this.makeAuthenticatedRequest('KATALE-GET-PRODUCT-RATINGS', {
      product_id: productId
    })
  }
}

export const apiService = new ApiService()
